import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchAllClientsWithFilters } from "store/appSlice";
import { Box, Button, Modal, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Table from "components/Table";
import NewClientModal from "components/Modals/NewClient";
import { ProtectedPaths } from "routes";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectClients } from "store/selectors";
import FilterComponent from "../../../../../components/FilterComponent/FilterComponent";

const ClientsTable = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const clients = useAppSelector(selectClients);
  const [sortParam, setSortParam] = useState<string | undefined>(undefined);

  // Initialize filters state from URL search params on first render
  const [filters, setFilters] = useState<{ [key: string]: string }>(() => {
    const restoredFilters: { [key: string]: string } = {};
    searchParams.forEach((value, key) => {
      restoredFilters[key] = value;
    });
    return restoredFilters;
  });

  // Keep filters state in sync when URL changes externally
  useEffect(() => {
    const restoredFilters: { [key: string]: string } = {};
    searchParams.forEach((value, key) => {
      restoredFilters[key] = value;
    });
    setFilters(restoredFilters);
  }, [searchParams]);

  useEffect(() => {
    // Combine filters and sort param for API call
    const allFilters = sortParam ? { ...filters, sort: sortParam } : filters;

    dispatch(fetchAllClientsWithFilters(currentPage, 25, allFilters));
  }, [dispatch, currentPage, sortParam, filters]);

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortParam(`${field},${direction}`);
  };

  const handleFilter = (newFilters: { [key: string]: string }) => {
    setFilters(newFilters);
    setCurrentPage(0);
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: (row: any) => {
        const { type, companyName, firstName, lastName, id } = row.row.original;

        let displayName = "";

        if (type === "BUSINESS" && companyName !== null) {
          displayName = companyName;
        } else if (firstName !== null && lastName !== null) {
          displayName = `${firstName} ${lastName}`;
        }

        return (
          <Link to={`/${ProtectedPaths.Clients}/${id}`}>
            {displayName || "-----"}
          </Link>
        );
      },
    },
    {
      accessorKey: "eic",
      header: "EIC",
    },
    {
      accessorKey: "registryCode",
      header: "Registry code",
    },
    {
      accessorKey: "meteringPoints",
      header: "Metering points",
      enableSorting: false,
      cell: (row: any) => {
        return row.row.original.meteringPointsCount;
      },
    },
  ];

  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">Clients</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
        >
          Add client
        </Button>
      </Box>
      <FilterComponent
        onFilter={handleFilter}
        columns={["name", "eic", "registryCode", "meteringPoints"]}
        columnPlaceholders={["Name", "EIC", "Registry code", "Metering points"]}
        filters={filters}
      />
      <Table
        data={clients?.content}
        columns={columns}
        isFetching={!clients}
        pageCount={clients?.totalPages}
        page={setCurrentPage}
        searchLabel="Search for client"
        onSort={handleSort}
      />
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <NewClientModal setOpenModal={setOpenModal} />
      </Modal>
    </>
  );
};

export default ClientsTable;
